import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { MapPin, Phone, Mail, Send, Loader2, CheckCircle } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ nom: "", email: "", telephone: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success(t('messageEnvoye'), { description: t('merciContact') });
      setFormData({ nom: "", email: "", telephone: "", message: "" });
    } catch (error) {
      toast.error("Erreur", { description: "Veuillez réessayer." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: MapPin, title: t('adresse'), content: `Avenue Louise 65 boîte 11\n1050 Bruxelles, ${t('belgique')}` },
    { icon: Phone, title: t('telephone'), content: "+32 2 880 66 50", href: "tel:+3228806650" },
    { icon: Mail, title: t('email'), content: "info@nettis.be", href: "mailto:info@nettis.be" }
  ];

  const departments = [
    { name: t('serviceComptable'), email: "finances@nettis.be" },
    { name: t('ressourcesHumaines'), email: "rh@nettis.be" },
    { name: t('serviceCommercial'), email: "sales@nettis.be" }
  ];

  return (
    <div data-testid="contact-page" className="pt-20">
      <SEO
        title="Contactez-nous"
        description="Contactez Nettis Facility pour un devis gratuit. Notre équipe est à votre disposition pour vos besoins en nettoyage professionnel. Tél: +32 2 880 66 50, Avenue Louise 65, Bruxelles."
        keywords="contact nettoyage Bruxelles, devis nettoyage gratuit, société nettoyage Belgique contact, Nettis Facility contact"
        canonicalUrl="https://www.nettis.be/contact"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-medium text-[#1E3A8A] uppercase tracking-widest mb-4 block">{t('contact')}</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">
              {t('contactTitle')}<span className="text-[#1E3A8A]">{t('nous')}</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">{t('contactDesc')}</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6">{t('envoyezMessage')}</h2>
              {isSubmitted ? (
                <div className="bg-green-50 rounded-2xl p-8 text-center">
                  <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">{t('messageEnvoye')}</h3>
                  <p className="text-slate-600 mb-6">{t('merciContact')}</p>
                  <Button onClick={() => setIsSubmitted(false)} className="bg-[#1E3A8A] hover:bg-[#2563EB] text-white rounded-full px-6">
                    {t('envoyerAutre')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="nom">{t('nomComplet')} *</Label>
                      <Input id="nom" name="nom" value={formData.nom} onChange={handleChange} placeholder={t('votreNom')} required className="bg-slate-50 border-slate-200" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('email')} *</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder={t('votreMail')} required className="bg-slate-50 border-slate-200" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telephone">{t('telephone')}</Label>
                    <Input id="telephone" name="telephone" value={formData.telephone} onChange={handleChange} placeholder="+32 xxx xx xx xx" className="bg-slate-50 border-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">{t('message')} *</Label>
                    <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder={t('votreMessage')} required rows={5} className="bg-slate-50 border-slate-200 resize-none" />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-[#1E3A8A] hover:bg-[#2563EB] text-white rounded-full py-3 font-semibold shadow-lg shadow-blue-900/20">
                    {isSubmitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />{t('envoiEnCours')}</>) : (<><Send className="mr-2 h-5 w-5" />{t('envoyerMessage')}</>)}
                  </Button>
                </form>
              )}
              <p className="mt-4 text-sm text-slate-500">* {t('champsObligatoires')}</p>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#0F172A] mb-6">{t('nosCoordonnees')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {contactInfo.map((info, index) => (
                    <div key={info.title} className="bg-slate-50 rounded-xl p-6">
                      <div className="w-12 h-12 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center mb-4">
                        <info.icon className="w-6 h-6 text-[#1E3A8A]" />
                      </div>
                      <h3 className="font-semibold text-[#0F172A] mb-2">{info.title}</h3>
                      {info.href ? (
                        <a href={info.href} className="text-slate-600 hover:text-[#1E3A8A] transition-colors whitespace-pre-line">{info.content}</a>
                      ) : (
                        <p className="text-slate-600 whitespace-pre-line">{info.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-4">{t('contactParDepartement')}</h3>
                <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {departments.map((dept, index) => (
                    <div key={dept.name} className="flex items-center justify-between p-4">
                      <span className="font-medium text-[#0F172A]">{dept.name}</span>
                      <a href={`mailto:${dept.email}`} className="text-[#1E3A8A] hover:underline text-sm">{dept.email}</a>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-sm text-slate-500"><strong>Nettis Facility</strong><br />TVA: BE0788.309.595</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A8A] to-[#172554]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t('rejoindreEquipeQuestion')}</h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">{t('rejoindreEquipeDesc')}</p>
          <Link to="/postuler#formulaire">
            <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-3 font-semibold shadow-lg transition-transform duration-200 hover:-translate-y-0.5">
              {t('postulerChezNettis')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
